/**
 * Frontend feature toggle helper — mirrors
 * `packages/backend/src/modules/featureFlags.ts` for the UI side.
 *
 * What: reads `digitalFactory.features.<name>` from the app's `configApi`
 * and answers "is this feature on?" with the same default policy as the
 * backend helper.
 *
 * Why a separate (duplicated) implementation instead of importing the
 * backend one: `packages/app` and `packages/backend` are separate
 * workspaces built independently (frontend bundle vs. Node backend);
 * importing backend source into the frontend bundle would drag in
 * Node-only deps (`fs`, `knex`, ...) and break the browser build. This
 * mirrors the project convention already used for RBAC permission names
 * (frontend replicates the string literal instead of importing from
 * backend — see `.cursor/rules/30-rbac-permissions.mdc`). Keep the
 * `DEFAULT_OFF_FEATURES` set below in sync with the backend one if a
 * flag's default policy changes.
 *
 * Only flags with `@visibility frontend` in `packages/app/config.d.ts`
 * are actually reachable here — `configApi.getOptionalBoolean` returns
 * `undefined` for anything not marked visible, which is indistinguishable
 * from "not set" and just falls through to the default below. That's a
 * safe failure mode (never accidentally exposes a secret, worst case a
 * flag silently uses its default), but it does mean: if you gate a NEW
 * frontend surface on a flag, double check it's declared in
 * `config.d.ts` too, or `useFeatureFlag` will always return the default.
 */
import { useApi, configApiRef, type ConfigApi } from '@backstage/core-plugin-api';

export type FeatureFlagName =
  | 'governance'
  | 'contentPages'
  | 'templatePreview'
  | 'notificationsTeams'
  | 'notificationsEmail'
  | 'azureDevOpsEntity'
  | 'sonarqube'
  | 'iframePreview'
  | 'kubernetes'
  | 'devImpersonation'
  | 'scaffolderExternalTemplates';

/** Mirrors backend `DEFAULT_OFF_FEATURES` in `featureFlags.ts`. */
const DEFAULT_OFF_FEATURES: ReadonlySet<FeatureFlagName> = new Set([
  'iframePreview',
  'kubernetes',
  'devImpersonation',
]);

export const isFeatureFlagEnabled = (
  configApi: ConfigApi,
  name: FeatureFlagName,
): boolean => {
  const value = configApi.getOptionalBoolean(
    `digitalFactory.features.${name}`,
  );
  if (value !== undefined) return value;
  return !DEFAULT_OFF_FEATURES.has(name);
};

/** Reads every flag at once — handy for a settings/debug panel. */
export const readFeatureFlags = (
  configApi: ConfigApi,
): Record<FeatureFlagName, boolean> => {
  const names: FeatureFlagName[] = [
    'governance',
    'contentPages',
    'templatePreview',
    'notificationsTeams',
    'notificationsEmail',
    'azureDevOpsEntity',
    'sonarqube',
    'iframePreview',
    'kubernetes',
    'devImpersonation',
    'scaffolderExternalTemplates',
  ];
  return Object.fromEntries(
    names.map(name => [name, isFeatureFlagEnabled(configApi, name)]),
  ) as Record<FeatureFlagName, boolean>;
};

export const useFeatureFlag = (name: FeatureFlagName): boolean => {
  const configApi = useApi(configApiRef);
  return isFeatureFlagEnabled(configApi, name);
};
